/**
 * useAvatarBridge
 *
 * React hook that installs bridge handlers on AvatarBridge and wires them to
 * the platform-shell's thread store. UI MECHANICS ONLY (T2, 2026-09-10): the
 * bridge can open, switch, list and name threads — it can no longer SEND. The
 * send path it used to hold (`submitToThread`) was the signed-in human's own
 * send button, so anything the avatar put through it wore the human's
 * authorship; a spoken ask now reaches a thread server-side under Prest0n's own
 * identity. Mount from GlobalChatSystem.tsx (or equivalent host) once
 * the thread store is ready; unmount cleans up automatically.
 *
 * Contract (see AVATAR_BRIDGE_PROTOCOL.md on this repo root):
 *   - Handlers receive parsed params, return a result object or throw.
 *   - Per-account scope is the CALLER's responsibility: callers pass the
 *     iframe + origin already scoped to the current user's avatar VM.
 *     This hook does NOT re-check origin (AvatarBridge does that).
 *
 * Events broadcast:
 *   - thread.created      (when newThread succeeds)
 *   - thread.switched     (when switchThread succeeds)
 *
 * Workflow events (workflow.started / clarifying-question / ...) are
 * broadcast from elsewhere by calling AvatarBridge.broadcastEvent();
 * this hook only handles request dispatch.
 *
 * Per-account scope hardening:
 *   - The hook does NOT accept cross-account threadIds. The platform
 *     shell only ever has one user's threads at a time (via
 *     useChatThreads), so threadId lookup is implicitly scoped.
 *   - A malicious iframe cannot submit a message at all: there is no send
 *     method on this surface any more (T2).
 */

import { useEffect, useRef } from 'react';
import AvatarBridge, {
  type BridgeMethod,
  type BridgeEventName,
} from '../services/avatarBridge/AvatarBridge';

type BridgeHandler = Parameters<typeof AvatarBridge.registerHandlers>[0];

/**
 * Minimal shape the bridge needs from the thread store. Kept narrow so
 * the hook is unit-testable without the full useChatThreads surface.
 */
export interface BridgeThreadStore {
  newThread: () => string;
  switchThread: (threadId: string) => void;
  renameThread: (threadId: string, title: string) => void;
  threads: ReadonlyArray<{
    id: string;
    title?: string;
    unread?: number;
    lastActivityAt?: number;
    status?: string;
  }>;
  activeThreadId: string | null;
}

export interface UseAvatarBridgeArgs {
  /** Thread store. */
  threadStore: BridgeThreadStore;
  /**
   * Label for debug logs (e.g. account id or user email). Optional.
   */
  label?: string;
  /**
   * Whether to install handlers. False disables the bridge temporarily
   * (e.g. during thread-store hydration). Defaults to true.
   */
  enabled?: boolean;
}


function findThread(store: BridgeThreadStore, id: string) {
  return store.threads.find((t) => t.id === id) || null;
}

/**
 * Build the handler registry from the provided thread store.
 *
 * Split out from the hook so it's importable + testable outside React.
 */
export function buildBridgeHandlers(opts: {
  threadStore: BridgeThreadStore;
  broadcastEvent: (e: { event: BridgeEventName; threadId: string; payload?: Record<string, unknown> }) => void;
  /**
   * L0.4 (2026-09-08): when given, every handler reads the thread store through this getter at
   * CALL time, so the hook can build the handlers once and read a live store across thread
   * switches. Without it (the tests' shape) the snapshot passed as `threadStore` is used.
   */
  getThreadStore?: () => BridgeThreadStore;
}): BridgeHandler {
  const { broadcastEvent } = opts;
  const store = (): BridgeThreadStore => (opts.getThreadStore ? opts.getThreadStore() : opts.threadStore);
  // 2026-05-23: module-level record updated by every newThread / switchThread
  // call. Used by event-emission code (via AvatarBridge.getActiveThreadId)
  // as a fallback so workflow.started / thread.clarifying-question /
  // workflow.completed events always resolve to a real thread id — even
  // in the window between newThread() returning and the React effect
  // re-registering the live getter.
  let lastHandledThreadId: string | null = null;

  // The duplicate-thread guard (2026-05-28) went with the submit handler below:
  // it existed only to absorb a placeholder threadId on a SUBMIT, and nothing
  // else ever read it.
  const primeActiveGetter = (id: string) => {
    lastHandledThreadId = id;
    AvatarBridge.registerActiveThreadIdGetter(() => lastHandledThreadId);
  };

  return {
    newThread: async (params) => {
      const title = typeof params.title === 'string' ? params.title : undefined;
      const id = store().newThread();
      primeActiveGetter(id);
      if (title) {
        try { store().renameThread(id, title); } catch { /* rename is best-effort */ }
      }
      broadcastEvent({ event: 'thread.created', threadId: id, payload: { title } });
      return { threadId: id, title, createdAt: new Date().toISOString() };
    },
    switchThread: async (params) => {
      const threadId = typeof params.threadId === 'string' ? params.threadId : '';
      if (!threadId) throw new Error('parameter threadId: string is required');
      let id = threadId;
      if (!findThread(store(), threadId)) {
        // 2026-05-23 auto-recovery: if the requested id isn't in the thread
        // store (closed thread / React race / stale id from a prior
        // session), create a fresh thread under the same title so the
        // avatar's flow doesn't crash with thread-not-found.
        // eslint-disable-next-line no-console
        console.warn('[avatar-bridge] switch_thread: id', threadId, 'not in store — creating fresh thread');
        id = store().newThread();
      }
      primeActiveGetter(id);
      store().switchThread(id);
      broadcastEvent({ event: 'thread.switched', threadId: id, payload: {} });
      return { threadId: id };
    },
    // ── submitToThread is GONE (T2, 2026-09-10) ────────────────────────────
    // It used to reach a thread by calling the SHELL's own
    // `sendProgrammaticMessage(text, { hidden: false })` — the visible human
    // send path. That is not "the avatar posting as the user with permission";
    // it IS the user's send: the bubble carried their authorship with no way to
    // record who composed it (see src/types/messageAuthorship.ts), it set their
    // own thinking widget, and a failed dispatch was swallowed to a console
    // line while the handler still answered "dispatched" — Miles's three
    // symptoms from the live test, all from this one path.
    // A spoken ask now reaches a thread SERVER-SIDE, under Prest0n's own
    // identity, through the voice server's handoff. The bridge keeps only UI
    // mechanics and therefore has no send capability at all: it does not take
    // the chat system as a dependency any more, so there is nothing here to
    // press. Pinned by tests/hooks/avatarBridgeHasNoSendPath.test.ts.
    listThreads: async () => ({
      threads: store().threads.slice(0, 50).map((t) => ({
        id: t.id,
        title: t.title || '',
        unread: t.unread || 0,
        lastActivityAt: t.lastActivityAt || 0,
        status: t.status || 'active',
      })),
    }),
    getActiveThread: async () => {
      const id = store().activeThreadId;
      const t = id ? findThread(store(), id) : null;
      return {
        threadId: id === null ? null : id,
        title: t?.title || null,
      };
    },
    // getWorkflowEvents is GONE with the submit it followed (T2, 2026-09-10).
    // It read an in-memory buffer that no thread event was ever written into,
    // so the avatar's "poll every 3 seconds and narrate each event" loop got an
    // empty array every time — a narration channel that could not narrate.
    // Progress facts come from the platform's own task record instead.
  };
}

/**
 * useAvatarBridge — wires the platform shell's thread store into the
 * AvatarBridge singleton. Called once from the component that owns it
 * (typically GlobalChatSystem, one-per-app).
 *
 * Iframe registration + origin allowlisting done separately at the avatar
 * iframe mount site via AvatarBridge.registerIframe / setAllowedOrigins.
 */
export function useAvatarBridge(args: UseAvatarBridgeArgs): void {
  const { threadStore, label, enabled = true } = args;
  const storeRef = useRef(threadStore);
  storeRef.current = threadStore;

  useEffect(() => {
    if (!enabled) return;
    // L0.4 (2026-09-08): built ONCE per mount. The handlers read the live store through the ref
    // getter, so a thread switch no longer rebuilds them — the duplicate-thread guard's
    // "recently opened" memory used to be wiped on every switch (the effect depended on
    // activeThreadId), which is exactly when the avatar's placeholder-id submit arrives.
    const handlers = buildBridgeHandlers({
      threadStore: storeRef.current,
      getThreadStore: () => storeRef.current,
      broadcastEvent: (e) => AvatarBridge.broadcastEvent(e),
    });
    AvatarBridge.registerHandlers(handlers);
    // Register active-thread-id getter so workflow handlers (outside React)
    // can read the current thread id when broadcasting events.
    AvatarBridge.registerActiveThreadIdGetter(() => storeRef.current.activeThreadId);
    // eslint-disable-next-line no-console
    console.log('[useAvatarBridge] handlers registered', label || '(no label)');
    return () => {
      AvatarBridge.clearHandlers([
        'newThread',
        'switchThread',
        'listThreads',
        'getActiveThread',
      ] as BridgeMethod[]);
      AvatarBridge.registerActiveThreadIdGetter(() => null);
    };
  }, [enabled, label]);
}

export default useAvatarBridge;
